import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useStore } from "../context/StoreContext";
import { getOrders } from "../lib/api";
import { Package, Heart, Gift, LogOut, MapPin } from "lucide-react";

export default function Account() {
  const { user, authLoading, logout, wishlist } = useStore();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => { if (!authLoading && !user) navigate("/login", { replace: true }); }, [user, authLoading, navigate]);
  useEffect(() => { if (user) getOrders().then(setOrders).catch(() => {}); }, [user]);

  if (authLoading || !user) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl">Loading…</div>;

  return (
    <div className="container-luxe py-16" data-testid="account-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mb-12 border-b border-hairline pb-8">
        <div className="flex items-center gap-4">
          {user.picture && <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full object-cover" />}
          <div>
            <p className="overline text-gold mb-1">My Account</p>
            <h1 className="h-display text-4xl">{user.name}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <button onClick={logout} className="btn-secondary" data-testid="logout-btn"><LogOut size={14} /> Sign Out</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
        {[[Gift, "Rewards Points", `${user.rewards_points ?? 0} pts`], [Heart, "Saved Items", `${wishlist.length}`], [Package, "Total Orders", `${orders.length}`]].map(([Icon, label, val], i) => (
          <div key={i} className="border border-hairline p-8" data-testid={`account-stat-${i}`}>
            <Icon size={22} strokeWidth={1.2} className="text-gold mb-4" />
            <p className="overline text-[10px] text-muted-foreground">{label}</p>
            <p className="font-serif text-3xl mt-1">{val}</p>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-3xl mb-6">Order History</h2>
      {orders.length === 0 ? (
        <div className="border border-hairline p-12 text-center" data-testid="no-orders">
          <p className="text-sm text-muted-foreground font-light mb-6">You haven't placed any orders yet.</p>
          <Link to="/shop" className="btn-primary">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.session_id} className="border border-hairline p-6 flex flex-col sm:flex-row justify-between gap-4" data-testid={`order-${o.session_id}`}>
              <div>
                <p className="overline text-[10px] text-muted-foreground">Order · {new Date(o.created_at).toLocaleDateString()}</p>
                <p className="font-serif text-xl mt-1">{o.items?.map((it) => it.name).join(", ")}</p>
              </div>
              <div className="text-right">
                <p className="font-display text-lg">${o.amount?.toFixed(2)}</p>
                <span className="overline text-[10px] text-gold">Paid · In Transit</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-14 border border-hairline p-8 flex items-start gap-4" data-testid="saved-addresses">
        <MapPin size={22} strokeWidth={1.2} className="text-gold mt-1" />
        <div>
          <h3 className="font-serif text-2xl mb-1">Saved Addresses</h3>
          <p className="text-sm text-muted-foreground font-light">Add a shipping address to check out faster next time.</p>
        </div>
      </div>
    </div>
  );
}
